import Vue from "vue";
import Component from "vue-class-component";
import { Prop } from "vue-property-decorator";
import Axios from "axios";
import Localizer from "@Scripts/Common/Localizer";
import GTagHelper from "@Scripts/Infrastructure/GTagHelper";

@Component({ name: "CatalogueFilterBox" })
export default class CatalogueFilterBox extends Vue {
    public localizer: any = Localizer;
    public isAreaListVisible: boolean = false;
    public isCategoryListVisible: boolean = false;
    public isFamilyListVisible: boolean = false;
    public isLoading: boolean = false;
    public filterData: any = { areas: [], categories: [], families: [], filterSelectors: [] };
    public areaIndex: number = 5;
    public categoryIndex: number = 5;
    public familyIndex: number = 5;
    public filters: any[] = [];
    public selectedFilter: string = null;
    public activatedFilters: any[] = [];
    public isShow: boolean = false;

    get routes() {
        return (<any>window).App.routes;
    }

    get selectedArea() {
        return this.areaName ? this.filterData.areas.find(x => x.name === this.areaName) : null;
    }

    get selectedCategory() {
        return this.categoryName ? this.filterData.categories.find(x => x.name === this.categoryName) : null;
    }

    get selectedFamily() {
        return this.familyName ? this.filterData.families.find(x => x.name === this.familyName) : null;
    }

    get isContentLoading(): any {
        return this.$store.getters["catalogueStore/isContentLoading"];
    }

    @Prop() readonly areaName: string;
    @Prop() readonly categoryName: string;
    @Prop() readonly familyName: string;
    @Prop() readonly page: string;

    public async created() {
        await this.getData();

        let vm = this;
        window.addEventListener("popstate", function (event) {
            vm.getData();
        });
    }

    public async getData() {
        const urlParams = new URLSearchParams(window.location.search);
        this.selectedFilter = urlParams.get("selectedfilter");

        let encodedfilters = urlParams.get("filters");
        this.$store.commit("catalogueStore/setEncodedFilters", encodedfilters);

        this.isLoading = true;

        this.filterData = (await Axios.post(this.routes.Catalogue_GetFilterData, { areaName: this.areaName, categoryName: this.categoryName, familyName: this.familyName, filters: encodedfilters })).data;

        this.filters = this.filterData.filters;

        this.setActivatedFilters();

        this.isLoading = false;
    }

    public openUrl(url: string) {
        window.location.href = url;
    }

    public openFamilyUrl(url: string) {
        if (this.filters.length !== 0) {
            window.location.href = url + "?filters=" + this.encodeFilters();
        } else {
            window.location.href = url;
        }
    }

    public getFilterTitle(filter: any): string {
        let selectedFilter = this.filterData.filterSelectors.find(x => x.name === filter.title);

        if (!selectedFilter) {
            return filter.value;
        }

        if (filter.type === "Range") {
            return filter.value.replace(",", " - ") + ' ' + filter.measureUnit;
        }

        return selectedFilter.listItems.find(x => x.value == filter.value)?.title + ' ' + filter.measureUnit;
    }

    public setFilter(name: string, value: any) {
        this.filters = this.filters.filter(x => x.name !== name);
        if (value) {
            this.filters.push({ name: name, value: value });
        }

        this.setActivatedFilters();

        this.openFilterUrl(name);
    }

    public setActivatedFilters() {
        this.activatedFilters = [];
        for (const filter of this.filters) {
            let filterType = this.filterData.filterSelectors.find(x => x.name === filter.name)?.type;
            let measureUnit = this.filterData.filterSelectors.find(x => x.name === filter.name)?.measureUnit;
            let selectedValues = null;

            if (filterType == "Range") {
                selectedValues = [{ title: filter.name, value: this.convertString(filter.value), type: filterType, measureUnit: measureUnit }];
            }
            else {
                selectedValues = filter.value.split(',').map(x => { return { title: filter.name, value: x, type: filterType, measureUnit: measureUnit } });
            }

            this.activatedFilters.push(...selectedValues);
        }

        this.activatedFilters.sort((a, b) => (a.title > b.title) ? 1 : ((b.title > a.title) ? -1 : 0));
    }

    public convertString(inputRange: string) {
        const parts = inputRange.split(',');

        const convertedParts = parts.map(part => parseFloat(part).toString());

        return convertedParts.join(',');
    }

    public removeFilterValue(filter: any) {
        let removedFilter = this.activatedFilters.find(x => x.title === filter.title && x.value === filter.value);
        this.activatedFilters = this.activatedFilters.filter(x => (x.title !== filter.title && x.value !== filter.value) || (x.title === filter.title && x.value !== filter.value));

        let index = this.filters.findIndex(x => x.name === filter.title);

        if (filter.type === "Range") {
            this.setFilter(this.filters[index].name, null);
        } else if (removedFilter.title === this.filters[index].name) {
            let newValue = this.filters[index].value.split(',').filter(item => item !== removedFilter.value);
            this.filters[index].value = newValue.join(',');

            this.setFilter(this.filters[index].name, this.filters[index].value);
        }
    }

    public encodeFilters(): string {
        return window.btoa(this.filters.map(x => x.name + ":" + x.value).join(";"));
    }

    public openFilterUrl(selectedFilter: string) {
        if (this.filters.length !== 0) {
            if ("URLSearchParams" in window) {
                let encodedFilters = this.encodeFilters();

                let searchParams = new URLSearchParams(window.location.search);
                searchParams.set("filters", encodedFilters);
                searchParams.set("selectedfilter", selectedFilter);

                this.$store.commit("catalogueStore/setEncodedFilters", encodedFilters);

                history.pushState({}, '', `${location.pathname}?${searchParams}`);
            }
        } else {
            this.removeFilters();
        }
    }

    public removeFilters() {
        this.filters = [];

        this.$store.commit("catalogueStore/setEncodedFilters", null);

        this.setActivatedFilters();

        history.pushState({}, '', `${location.pathname}`);
    }

    public setIsAreaListVisible() {
        this.isAreaListVisible = !this.isAreaListVisible;
    }

    public setIsCategoryListVisible() {
        this.isCategoryListVisible = !this.isCategoryListVisible;
    }

    public setIsFamilyListVisible() {
        this.isFamilyListVisible = !this.isFamilyListVisible;
    }

    public plus(section: string) {
        switch (section) {
            case "area":
                this.areaIndex = 999;
                break;
            case "category":
                this.categoryIndex = 999;
                break;
            case "family":
                this.familyIndex = 999;
                break;
        }
    }

    public less(section: string) {
        switch (section) {
            case "area":
                this.areaIndex = 5;
                break;
            case "category":
                this.categoryIndex = 5;
                break;
            case "family":
                this.familyIndex = 5;
                break;
        }
    }

    public setShow() {
        this.isShow = !this.isShow;
    }
};